import React from 'react'
import { QuestionnaireItem } from '@medplum/fhirtypes'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import { getNestedItems } from 'components/Assessments/helpers'
import { useQAAssessmentActionsContext } from 'contexts/QAAssessmentActionsContext'
import { useQAAssessmentCommentsContext } from 'contexts/QAAssessmentCommentsContext'
import { getCommentQuestionLinkId } from 'components/QA/Assessments/helpers'
import { CNASkilledTasksFormField } from './formField'
import { CNASkilledTasksQuestionnaireItem } from './questionnaire'
import { CodingRadioQuestionWithNotes } from '../../components'
import { PATSectionComponentProps } from '../../types/components'

export default function CNASkilledTasksSection({
  item: section,
  sectionId: _sectionId,
  sectionIndex: _sectionIndex
}: PATSectionComponentProps): JSX.Element {
  const {
    cantEditAssessmentFields,
    assessmentMustBeFinalized,
    assessmentStatusState: { inQA, inQAEdit }
  } = useAssessmentContext()
  const { createCommentClickHandler, disableHover } = useQAAssessmentActionsContext()
  const { sortedComments: comments, focusedCommentId } = useQAAssessmentCommentsContext()
  const focusedComment = comments.find((c) => c.id === focusedCommentId)
  const focusedCommentLinkId = focusedComment ? getCommentQuestionLinkId(focusedComment) : null

  // Helper function to create comment click handler for a question field
  const createQuestionCommentClickHandler = (field: QuestionnaireItem | undefined) => (): void => {
    if (field?.linkId) {
      createCommentClickHandler(field.linkId)
    }
  }

  // Helper function to find additional question from conditionalQuestions
  const findAdditionalQuestion = (
    conditionalQuestions: QuestionnaireItem[],
    expectedLinkId: string
  ): QuestionnaireItem | undefined => {
    return conditionalQuestions.find((cq) => cq.linkId === expectedLinkId)
  }

  // Deterministic mapping of linkId to JSX renderer function.
  const fieldComponentMap: Record<
    string,
    (
      question: QuestionnaireItem,
      notes: QuestionnaireItem,
      conditionalQuestions: QuestionnaireItem[]
    ) => JSX.Element
  > = {
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BATHING_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_BATHING_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_BATHING_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_QUESTION]: (
      question,
      notes,
      conditionalQuestions
    ) => {
      const additionalQuestion = findAdditionalQuestion(
        conditionalQuestions,
        CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_ADDITIONAL
      )
      return (
        <CodingRadioQuestionWithNotes
          key={question.linkId}
          field={question}
          formFieldName={
            CNASkilledTasksFormField.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_QUESTION
          }
          notesField={notes.linkId ? notes : undefined}
          notesFormFieldName={
            CNASkilledTasksFormField.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_NOTES
          }
          disabled={cantEditAssessmentFields}
          required={assessmentMustBeFinalized}
          inQA={inQA}
          inQAEdit={inQAEdit}
          disableHover={disableHover}
          isFocused={question.linkId === focusedCommentLinkId}
          onCommentClick={createQuestionCommentClickHandler(question)}
          detailedMode={true}
          additionalQuestion={additionalQuestion}
          additionalFormFieldName={
            CNASkilledTasksFormField.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_ADDITIONAL
          }
          assessmentMustBeFinalized={assessmentMustBeFinalized}
        />
      )
    },
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_DRESSING_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_DRESSING_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_DRESSING_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_EATING_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_EATING_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_EATING_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_TUBE_FEEDINGS_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_TUBE_FEEDINGS_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_TUBE_FEEDINGS_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_MEDICATION_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEDICATION_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEDICATION_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_MOBILITY_QUESTION]: (
      question,
      notes,
      conditionalQuestions
    ) => {
      const additionalQuestion = findAdditionalQuestion(
        conditionalQuestions,
        CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_MOBILITY_ADDITIONAL
      )
      return (
        <CodingRadioQuestionWithNotes
          key={question.linkId}
          field={question}
          formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_MOBILITY_QUESTION}
          notesField={notes.linkId ? notes : undefined}
          notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_MOBILITY_NOTES}
          disabled={cantEditAssessmentFields}
          required={assessmentMustBeFinalized}
          inQA={inQA}
          inQAEdit={inQAEdit}
          disableHover={disableHover}
          isFocused={question.linkId === focusedCommentLinkId}
          onCommentClick={createQuestionCommentClickHandler(question)}
          detailedMode={true}
          additionalQuestion={additionalQuestion}
          additionalFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_MOBILITY_ADDITIONAL}
          assessmentMustBeFinalized={assessmentMustBeFinalized}
        />
      )
    },
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_MEAL_PREP_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEAL_PREP_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEAL_PREP_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_GROOMING_QUESTION]: (
      question,
      notes,
      conditionalQuestions
    ) => {
      const additionalQuestion = findAdditionalQuestion(
        conditionalQuestions,
        CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_GROOMING_ADDITIONAL
      )
      return (
        <CodingRadioQuestionWithNotes
          key={question.linkId}
          field={question}
          formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_GROOMING_QUESTION}
          notesField={notes.linkId ? notes : undefined}
          notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_GROOMING_NOTES}
          disabled={cantEditAssessmentFields}
          required={assessmentMustBeFinalized}
          inQA={inQA}
          inQAEdit={inQAEdit}
          disableHover={disableHover}
          isFocused={question.linkId === focusedCommentLinkId}
          onCommentClick={createQuestionCommentClickHandler(question)}
          detailedMode={true}
          additionalQuestion={additionalQuestion}
          additionalFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_GROOMING_ADDITIONAL}
          assessmentMustBeFinalized={assessmentMustBeFinalized}
        />
      )
    },
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_QUESTION]: (
      question,
      notes,
      conditionalQuestions
    ) => {
      const additionalQuestion = findAdditionalQuestion(
        conditionalQuestions,
        CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_ADDITIONAL
      )
      return (
        <CodingRadioQuestionWithNotes
          key={question.linkId}
          field={question}
          formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_QUESTION}
          notesField={notes.linkId ? notes : undefined}
          notesFormFieldName={
            CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_NOTES
          }
          disabled={cantEditAssessmentFields}
          required={assessmentMustBeFinalized}
          inQA={inQA}
          inQAEdit={inQAEdit}
          disableHover={disableHover}
          isFocused={question.linkId === focusedCommentLinkId}
          onCommentClick={createQuestionCommentClickHandler(question)}
          detailedMode={true}
          additionalQuestion={additionalQuestion}
          additionalFormFieldName={
            CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_ADDITIONAL
          }
          assessmentMustBeFinalized={assessmentMustBeFinalized}
        />
      )
    },
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_QUESTION]: (
      question,
      notes,
      conditionalQuestions
    ) => {
      const additionalQuestion = findAdditionalQuestion(
        conditionalQuestions,
        CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_ADDITIONAL
      )
      return (
        <CodingRadioQuestionWithNotes
          key={question.linkId}
          field={question}
          formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_QUESTION}
          notesField={notes.linkId ? notes : undefined}
          notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_NOTES}
          disabled={cantEditAssessmentFields}
          required={assessmentMustBeFinalized}
          inQA={inQA}
          inQAEdit={inQAEdit}
          disableHover={disableHover}
          isFocused={question.linkId === focusedCommentLinkId}
          onCommentClick={createQuestionCommentClickHandler(question)}
          detailedMode={true}
          additionalQuestion={additionalQuestion}
          additionalFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_ADDITIONAL}
          assessmentMustBeFinalized={assessmentMustBeFinalized}
        />
      )
    },
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_ROM_EXERCISE_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_ROM_EXERCISE_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_ROM_EXERCISE_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_RESPIRATORY_CARE_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_RESPIRATORY_CARE_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_RESPIRATORY_CARE_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_OXYGEN_THERAPY_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_OXYGEN_THERAPY_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_OXYGEN_THERAPY_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_ORAL_SUCTIONING_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_ORAL_SUCTIONING_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_ORAL_SUCTIONING_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_FREQUENCY_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_FREQUENCY_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_FREQUENCY_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_HYGIENE_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_HYGIENE_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_HYGIENE_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_ELIMINATION_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_ELIMINATION_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_ELIMINATION_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_PROGRAM_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_PROGRAM_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_PROGRAM_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_CATHETER_CARE_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_CATHETER_CARE_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_CATHETER_CARE_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_MENSES_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_MENSES_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_MENSES_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_URINARY_HYGIENE_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_URINARY_HYGIENE_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_URINARY_HYGIENE_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_POSITIONING_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_POSITIONING_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_POSITIONING_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_TRANSFERS_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_TRANSFERS_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_TRANSFERS_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_PHYSICIAN_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_PHYSICIAN_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_PHYSICIAN_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    ),
    [CNASkilledTasksQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_NURSE_QUESTION]: (
      question,
      notes,
      _conditionalQuestions
    ) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_NURSE_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_NURSE_NOTES}
        disabled={cantEditAssessmentFields}
        required={assessmentMustBeFinalized}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
        detailedMode={true}
        assessmentMustBeFinalized={assessmentMustBeFinalized}
      />
    )
  }

  // Iterate over section.items in questionnaire order
  const taskGroups =
    section?.item && Array.isArray(section.item)
      ? section.item.filter(
          (item): item is QuestionnaireItem => item.type === 'group' && !!item.item
        )
      : []

  // Guard clause: return early if section is undefined, null, or has no items
  if (!section || !section.item || !Array.isArray(section.item) || taskGroups.length === 0) {
    return <></>
  }

  // Render questions in questionnaire order
  return (
    <>
      {taskGroups.map((taskGroup) => {
        const { question, notes, conditionalQuestions } = getNestedItems(taskGroup.item || [])

        if (!question.linkId) {
          return null
        }

        const renderField = fieldComponentMap[question.linkId]

        if (!renderField) {
          return null
        }

        return renderField(question, notes, conditionalQuestions)
      })}
    </>
  )
}
